import React from "react";
import ProjectPageDataComponent from 'Pages/apps/default/projectPages/ProjectPageDataComponent';
import {Link} from "Core/router";
import {connect} from "react-redux";
import {getPageActions} from "Core/helpers/redux";
import {selectors} from "Core/store/reducers";
import * as appConfig from "../../config";
import * as pageConfig from "./config";
import * as actions from "Pages/apps/default/customerGroups/actions";
import * as popupActions from "Pages/apps/default/customerGroups/popups/ItemPopup/actions";
import {getMenuSidebarShrankFromStorage} from "Layout/elements/MainSidebar/helpers";
import {reducerStoreKey} from "Pages/apps/default/customerGroups/reducer";
import {
	areAllObjectPropsEmpty,
	getArray,
	getNumber,
	getString,
	isset,
	trimArray
} from 'Core/helpers/data';
import * as filterDataMap from "Pages/apps/default/customerGroups/dataMap/filter";
import {scrollToSelector} from "Core/helpers/dom";
import {getPopupInstance} from "Core/helpers/popup";
import ItemPopup from "Pages/apps/default/customerGroups/popups/ItemPopup";
import {Tooltip} from "Core/components/global/Tooltip";
import Button, {BUTTON_DISPLAY_TYPE, BUTTON_STYLE} from "Core/components/display/Button";
import {
	icon_font_create_symbol,
	icon_font_delete_symbol,
	icon_font_edit_symbol,
	icon_font_info_circle_symbol
} from 'Config/app';
import ConfirmDialog from "Core/components/dialogs/ConfirmDialog";
import Label from "Core/components/display/Label";
import SimpleStaticSearch, {
	SIMPLE_STATIC_SEARCH_DISPLAY_TYPE,
	SIMPLE_STATIC_SEARCH_LAYOUT,
	SimpleStaticSearchOptionObject
} from 'Core/components/advanced/SimpleStaticSearch';
import DataTable, {DATA_TABLE_CELL_TYPE} from "Core/components/advanced/DataTable";
import {PAGINATION_TYPE} from "Core/components/action/Pagination";
import {isSuccessful} from "Core/helpers/io";
import {AsyncMountError} from "Core/errors";
import {
	DataTableCellAnyTypeOptionsDataObject,
	DataTableCellNumberTypeOptionsDataObject
} from 'Core/components/advanced/DataTable/DataTableCell/dataObjects';
import ProjectsACL from 'Acl/projects';
import {
	CUSTOMER_GROUP_STATUS_BKG_COLOR_CLASS,
	CUSTOMER_GROUP_STATUS_COLOR_CLASS,
	CUSTOMER_GROUP_STATUSES
} from 'Pages/apps/default/customerGroups/const';
import IconOptionComponent from 'Core/components/input/SelectInput/options/IconOption';
import IconSingleValueComponent from 'Core/components/input/SelectInput/singleValues/IconSingleValue';
import {SelectDynamicValueFunction} from 'Core/components/input/SelectInput';
import {getCurrentProjectFromUrl, getCurrentProjectIdFromUrl} from 'Helpers/project';
import Breadcrumbs from 'Core/components/display/Breadcrumbs';

/**
 * Redux 'mapStateToProps' function
 *
 * @param {object} state - Redux entire store state.
 * @return {Object<string, any>} Mapped props that can be used in a component.
 */
const mapStateToProps = state => ({
	projectList: selectors.projectSelector.getProjectList(state),
	isMobileBreakpoint: selectors.breakpoint.isMobileBreakpoint(state),
	mainSidebarShrank: getMenuSidebarShrankFromStorage(selectors.mainSidebar.shrank(state)),
	mainList: selectors[reducerStoreKey].getCustomerGroupsList(state),
	mainListPagination: selectors[reducerStoreKey].getCustomerGroupsListPagination(state),
	mainListSort: selectors[reducerStoreKey].getCustomerGroupsListSort(state),
	mainListFilter: selectors[reducerStoreKey].getCustomerGroupsListFilter(state),
	singleProject: selectors.projectSelector.getSingleProject(state),
});

class CustomerGroupsPage extends ProjectPageDataComponent {
	constructor(props) {
		super(props, {
			data: {
				/**
				 * Flag showing if a filter is loading
				 */
				filterLoading: false,

				/**
				 * Flag showing if a page is loading data
				 * @type {boolean}
				 */
				loading: false,
			},

			/**
			 * Flag that specifies if the main data table height is limited to the available space
			 */
			limitToAvailableSpace: true
		}, {
			domPrefix: 'customer-groups-page',
			translationPath: pageConfig.translationPath,
			routerPath: pageConfig.routerPath,
			checkLogin: false,
			disableLoad: true,
		}, 'page_title');

		// Refs
		this.mainListFilterRef = null;

		// Data methods
		this.reloadMainList = this.reloadMainList.bind(this);
		this.loadMainListPage = this.loadMainListPage.bind(this);
		this.sortMainList = this.sortMainList.bind(this);
		this.filterMainList = this.filterMainList.bind(this);
		this.removeMainListFilter = this.removeMainListFilter.bind(this);
		this.isMainListFilterEmpty = this.isMainListFilterEmpty.bind(this);

		// Popup methods
		this.openMainListItemPopup = this.openMainListItemPopup.bind(this);
		this.closeMainListItemPopup = this.closeMainListItemPopup.bind(this);
		this.handleMainListItemPopupClose = this.handleMainListItemPopupClose.bind(this);

		// Action methods
		this.deleteMainListItem = this.deleteMainListItem.bind(this);

		// Render methods
		this.renderActions = this.renderActions.bind(this);
	}

	componentDidUpdate(prevProps, prevState, snapshot) {
		return super.componentDidUpdate(prevProps, prevState, snapshot)
			.then(() => {
				const prevProjectId = getString(getCurrentProjectIdFromUrl(prevProps.location));
				const projectId = getString(getCurrentProjectIdFromUrl(this.props.location));

				if (prevProjectId !== projectId) {
					const {resetCustomerGroupsListAction} = this.props;

					// Clear all page data and load the page again
					resetCustomerGroupsListAction();
					this.clear().then(() => this.loadPageData().catch(() => {}));
				}
			});
	}


	// Data methods -----------------------------------------------------------------------------------------------------
	/**
	 * Method that will be called on component mount and should be used to load any data required by the page
	 * @return {Promise<any>}
	 * @throws {AsyncMountError}
	 */
	loadPageData() {
		const {location, mainList, loadCustomerGroupsListAction} = this.props;
		const projectId = getCurrentProjectIdFromUrl(location);

		// If the main list was already loaded (the user already opened the page before)
		if (isset(mainList)) {
			// Open a filter if it is not empty
			if (!this.isMainListFilterEmpty() && this.mainListFilterRef) this.mainListFilterRef.open();
			// Reload the main list with currently applied filter, sort and pagination
			return this.reloadMainList()
				.then(res => { if (!isset(res)) throw new AsyncMountError(); });
		}
		// Load the main list if it is not already loaded
		else {
			return this.setValue('loading', true)
				.then(() => this.executeAbortableActionMount(loadCustomerGroupsListAction, projectId))
				.then(() => this.setValue('loading', false));
		}
	}

	/**
	 * Reload the main list using current options (page, sort, ...)
	 * @return {Promise<*>}
	 */
	reloadMainList() {
		const {location, loadCustomerGroupsListAction, mainListPagination, mainListSort, mainListFilter} = this.props;
		const {pageNo, perPage} = mainListPagination;
		const {sortBy, sortDir} = mainListSort;
		const projectId = getCurrentProjectIdFromUrl(location);
		const oFilter = filterDataMap.output(mainListFilter);

		return this.executeAbortableAction(
			loadCustomerGroupsListAction, projectId, oFilter, pageNo, perPage, sortBy, sortDir
		)
			.then(res => {
				this.mainListFilterRef?.reload();
				return res;
			});
	}

	/**
	 * Reload the main list using current options (page, sort, ...) if any
	 * @param {number} [pageNo=1] - Page number to load (starts with 1).
	 * @return {Promise<*>}
	 */
	loadMainListPage(pageNo = 1) {
		const {location, loadCustomerGroupsListAction, mainListPagination, mainListSort, mainListFilter} = this.props;
		const {perPage} = mainListPagination;
		const {sortBy, sortDir} = mainListSort;
		const projectId = getCurrentProjectIdFromUrl(location);
		const oFilter = filterDataMap.output(mainListFilter);

		return this.executeAbortableAction(
			loadCustomerGroupsListAction, projectId, oFilter, pageNo, perPage, sortBy, sortDir
		);
	}

	/**
	 * Sort main list
	 * @param {string} sortBy - Name of the sort column.
	 * @param {string} sortDir - Direction of the sort.
	 * @return {Promise<*>}
	 */
	sortMainList(sortBy, sortDir) {
		const {location, loadCustomerGroupsListAction, mainListPagination, mainListFilter} = this.props;
		const {pageNo, perPage} = mainListPagination;
		const projectId = getCurrentProjectIdFromUrl(location);
		const oFilter = filterDataMap.output(mainListFilter);

		return this.executeAbortableAction(
			loadCustomerGroupsListAction, projectId, oFilter, pageNo, perPage, sortBy, sortDir
		);
	}

	/**
	 * Filter main list
	 * @param {Object} filter - Filter object where keys are filter field names and values are filter values.
	 * @return {Promise<*>}
	 */
	filterMainList(filter) {
		const {
			location, loadCustomerGroupsListAction, setCustomerGroupsFilterAction, mainListPagination, mainListSort,
		} = this.props;
		const {perPage} = mainListPagination;
		const {sortBy, sortDir} = mainListSort;
		const projectId = getCurrentProjectIdFromUrl(location);
		const oFilter = filterDataMap.output(filter);
		
		// Set filter so that the change will be detected after IO
		setCustomerGroupsFilterAction(filter);

		return this.setValue('filterLoading', true)
			.then(() => this.executeAbortableAction(
				loadCustomerGroupsListAction, projectId, oFilter, 1, perPage, sortBy, sortDir
			))
			.then(() => this.setValue('filterLoading', false))
			.then(() => {
				if (areAllObjectPropsEmpty(oFilter, true, false)) {
					if (this.mainListFilterRef) {
						this.mainListFilterRef.close()
							.then(() => this.setState({limitToAvailableSpace: true}));
					}
				} else {
					scrollToSelector('#main-page-table', false, 80);
				}
			});
	}

	/**
	 * Remove the main list filter
	 * @return {Promise<*>}
	 */
	removeMainListFilter() {
		return this.filterMainList(null);
	}

	/**
	 * Check if the main list filter is applied
	 * @return {Boolean}
	 */
	isMainListFilterEmpty() {
		return areAllObjectPropsEmpty(this.getProp('mainListFilter'), true, false);
	}


	// Router methods ---------------------------------------------------------------------------------------------------
	/**
	 * Method that will be called if current URL matches the 'create' sub-url of the page
	 * @note Create sub-url uses '/new' router path relative to the router path of the page (see 'options.routerPath').
	 *
	 * @param {Object} [prevLocation] - Previous router location.
	 * @return {string|Promise<string>} GUI ID of the component (popup, dialog, ...) that is rendered when page is on
	 * 'create' sub-url if such a component exists.
	 */
	handleCreateUrl(prevLocation) {
		const {location} = this.props;
		const projectId = getCurrentProjectIdFromUrl(location);
		const canEdit = ProjectsACL.checkPermission(ProjectsACL, projectId, ['CUSTOMER_WRITE']);
		
		if (canEdit) {
			// Open the main list item popup if it is not already opened
			// @note This is done to avoid opening multiple dialogs when switching from item edit to the new item URL. 
			// Dynamic URL component data should be changed using the standard 'updateDynamics' method.
			if (!this.urlComponentGUIID) {
				return this.openMainListItemPopup(true);
			}
			// If the URL component is opened, update its dynamic items (tabs, action buttons, ...)
			else {
				const mainListItemPopup = getPopupInstance(this.urlComponentGUIID);
				return mainListItemPopup.updateDynamics({isNew: true}).then(() => this.urlComponentGUIID);
			}
		} else {
			this.redirectToBase();
		}
	}

	/**
	 * Method that will be called if current URL matches the 'item' sub-url of the page
	 * @note Item sub-url uses '/item' router path and 'id' as router path param ('/item/:id') on top of to the router
	 * path of the page (see 'options.routerPath').
	 *
	 * @param {string|number} id - Item ID.
	 * @param {Object} [prevLocation] - Previous router location.
	 * @return {string|Promise<string>} GUI ID of the component (popup, dialog, ...) that is rendered when page is on
	 * 'item' sub-url if such a component exists.
	 */
	handleItemUrl(id, prevLocation) {
		const {loadCustomerGroupsItemAction} = this.props;

		// @note This is done to avoid opening multiple dialogs when switching from a new item to edit item URL. Dynamic URL
		// component data should be changed using the standard 'updateDynamics' method.
		if (!this.urlComponentGUIID) {
			this.urlComponentGUIID = this.openMainListItemPopup();
		}
		// If the URL component is opened, update its dynamic items (tabs, action buttons, ...)
		else {
			const mainListItemPopup = getPopupInstance(this.urlComponentGUIID);
			mainListItemPopup.updateDynamics({isNew: false}).then();
		}

		// Try to load the main list item
		return new Promise(resolve =>
			// Timeout is added to allow for the popup open animation to finish
			setTimeout(() => resolve(
				this.executeAbortableAction(loadCustomerGroupsItemAction, id)
					.then(mainListItem => {
						// If the main list item is successfully loaded
						if (mainListItem) {
							return this.urlComponentGUIID;
						}
						// If the main list item could not be loaded (usually if the item with ID from URL does not exist)
						else {
							// Close the main list item popup if it is opened
							if (this.urlComponentGUIID) this.closeUrlComponent();
							// Redirect to page base url (removes main list item ID from URL if it exists)
							this.redirectToBase();
							return '';
						}
					})
			))
		);
	}

	/**
	 * Method that will be called if the current URL matches the base URL of the page
	 *
	 * @param {Object} [prevLocation] - Previous router location.
	 * @return {string|Promise<string>} GUI ID of the component (popup, dialog, ...) that is rendered when page is on its
	 * base URL if such a component exists.
	 */
	handleBaseUrl(prevLocation) {
		this.closeMainListItemPopup();
		return '';
	}

	/**
	 * Method that will be called when a page component unmounts and should handle closing of any page url or sub-url
	 * component if it exists.
	 */
	closeUrlComponent() {
		this.closeMainListItemPopup();
	}


	// Popup methods ----------------------------------------------------------------------------------------------------
	/**
	 * Open main list item popup
	 * @param {boolean} [isNew=false] - Flag that specifies if new main list item popup should be opened.
	 */
	openMainListItemPopup(isNew = false) {
		const {location, openPopupAction} = this.props;
		const projectId = getCurrentProjectIdFromUrl(location);
		
		return openPopupAction(ItemPopup, {
			projectId,
			isNew,
			onClose: this.handleMainListItemPopupClose,
			deleteItemAction: this.deleteMainListItem,
			redirectToItem: this.redirectToItem,
			redirectToBase: this.redirectToBase,
		});
	}

	/**
	 * Close the main list item popup
	 */
	closeMainListItemPopup() {
		const {closePopupAction, clearCustomerGroupsItemAction, clearCustomerGroupProgressAction} = this.props;

		// Close item popup
		const urlComponentGUIID = this.getUrlComponentGUIID();
		if (urlComponentGUIID) {
			closePopupAction(urlComponentGUIID);
			this.clearUrlComponentGUIID();
		}

		// Clear popup Redux data
		clearCustomerGroupsItemAction();
		clearCustomerGroupProgressAction();
	}

	/**
	 * Handle the main list item popup 'onClose' event
	 * @return {void}
	 */
	handleMainListItemPopupClose() {
		this.clearUrlComponentGUIID();
		this.redirectToBase();
		// Timeout is added to allow for the popup close animation to finish
		setTimeout(this.reloadMainList);
	}


	// Action methods ---------------------------------------------------------------------------------------------------
	/**
	 * Delete main list item
	 * 
	 * @param {CustomerGroupsListItemDataObject} item - Main list popup item.
	 * @return {Promise<void>}
	 */
	deleteMainListItem(item) {
		const {
			openDialogAction, closeDialogAction, deleteCustomerGroupsItemAction, addSuccessMessageAction
		} = this.props;
		
		return new Promise(resolve => {
			const dialogGUIID = openDialogAction('', ConfirmDialog, {
				yesTimer: 3,
				message: this.t('confirm_delete', '', '', {name: item.name}),
				supportHtml: true,
				onYes: () => {
					this.executeAbortableAction(deleteCustomerGroupsItemAction, item.id)
						.then(response => {
							if (isSuccessful(response)) {
								addSuccessMessageAction(this.tt('delete_success_msg', 'ItemPopup'));
								// Reload main list data
								// @note This is done asynchronously on purpose because there is no reason to wait for this to 
								// finish before continuing.
								this.reloadMainList()
									// Go to the previous page if there are no table rows after one has been deleted
									.then(() => {
										const mainList = getArray(this.props, 'mainList');
										const pageNo = getNumber(this.props, 'mainListPagination.pageNo', 1);
										if (mainList.length === 0 && pageNo > 1) return this.loadMainListPage(pageNo - 1);
									});
							}
							return Promise.resolve(response);
						})
						.then(() => closeDialogAction(dialogGUIID))
						.finally(() => resolve('yes'));
				},
				onNo: () => {
					closeDialogAction(dialogGUIID);
					resolve('no');
				}
			}, {
				id: 'item-delete-dialog',
				closeOnEscape: true,
				closeOnClickOutside: true,
				hideCloseBtn: true,
				maxWidth: 500
			});
			this.setOption(
				'dialogsToCloseOnUnmount', 
				trimArray([...this.getOption('dialogsToCloseOnUnmount'), dialogGUIID], 'left')
			);
		});
	}
	

	// Render methods ---------------------------------------------------------------------------------------------------
	/**
	 * Render data table actions cell
	 * @param {CustomerGroupsListItemDataObject} row - Data table row.
	 * @return {JSX.Element}
	 */
	renderActions(row) {
		const {location} = this.props;
		const projectId = getCurrentProjectIdFromUrl(location);
		const canEdit = ProjectsACL.checkPermission(ProjectsACL, projectId, ['CUSTOMER_WRITE']);
		
		return (
			<div className="actions standard-actions-row">
				{canEdit ?
					<Tooltip
						tag="div"
						title={this.t('delete_tooltip')}
						size="small"
						position="top-center"
						arrow={true}
						interactive={false}
						touchHold={true}
						delay={250}
					>
						<Button
							className="action-btn no-border-radius"
							displayStyle={BUTTON_STYLE.ACTION}
							displayType={BUTTON_DISPLAY_TYPE.NONE}
							icon={icon_font_delete_symbol}
							onClick={() => this.deleteMainListItem(row)}
						/>
					</Tooltip>
					: null
				}

				<Tooltip
					tag="div"
					title={canEdit ? this.t('edit_tooltip') : this.t('view_tooltip')}
					size="small"
					position="top-center"
					arrow={true}
					interactive={false}
					touchHold={true}
               delay={250}
				>
					<Button
						className="action-btn no-border-radius"
						displayStyle={BUTTON_STYLE.ACTION}
						displayType={BUTTON_DISPLAY_TYPE.NONE}
						icon={canEdit ? icon_font_edit_symbol : icon_font_info_circle_symbol}
						onClick={() => this.redirectToItem(row.id)}
					/>
				</Tooltip>
			</div>
		);
	}

	/**
	 * Render page title
	 * @description This method specifies how page title will be rendered if page title should be rendered. It does not
	 * determine if the page title should be rendered.
	 * @return {JSX.Element}
	 */
	renderPageTitle() {
		const {location, projectList, singleProject} = this.props;
		const {title} = this.state;
		/** @type {ProjectListItemDataObject} */
		const currentProject = getCurrentProjectFromUrl(projectList, location);
		const canEdit = (
			!!currentProject ? ProjectsACL.checkPermission(ProjectsACL, currentProject.id, ['CUSTOMER_WRITE']) : false
		);

		return (
			<h1 className={`page-title with-actions ${!canEdit ? 'reload-only' : ''}`}>
				<div className="content">
					{singleProject ?
						<Label content={(title ? this.translate(title, this.titlePathPrefix) : '')} />
						:
						<Breadcrumbs
							includeHome={false}
							items={[
								{
									to: this.getProjectTo(),
									label: getString(currentProject, 'name')
								},
								{
									label: (title ? this.translate(title, this.titlePathPrefix) : '')
								}
							]}
						/>
					}
				</div>
				<div className="actions">
					<div className="action-button">
						<Tooltip
							tag="div"
							title={this.t('Reload data', 'general')}
							size="small"
							position="top-center"
							arrow={true}
							interactive={false}
							touchHold={true}
							delay={250}
						>
							<Button
								big={true}
								icon="refresh"
								displayType={BUTTON_DISPLAY_TYPE.TRANSPARENT}
								displayStyle={BUTTON_STYLE.SUBTLE}
								onClick={this.reloadMainList}
							/>
						</Tooltip>
					</div>

					{canEdit ?
						<div className="action-button main-button">
							<Link to={this.getCreateRedirectTo()} className="button action solid big">
								<Label icon={icon_font_create_symbol} content={this.t('create_new')} />
							</Link>
						</div>
						: null
					}
				</div>
			</h1>
		);
	}
	
	render() {
		const {
			isMobileBreakpoint, mainList, mainListPagination, mainListSort, mainListFilter, mainSidebarShrank, 
			toggleMainSidebarSizeAction
		} = this.props;
		const {limitToAvailableSpace} = this.state;

		let filterOptions = [
			new SimpleStaticSearchOptionObject(
				'status',
				this.tt('statusField', 'ItemPopup.MainTab'),
				SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.SELECT,
				{
					isClearable: true,
					options: CUSTOMER_GROUP_STATUSES.map(s => ({label: this.tt(s, 'ItemPopup.MainTab.status'), value: s})),
					components: {Option: IconOptionComponent, SingleValue: IconSingleValueComponent},
					labelProps: {
						symbol: 'circle',
						className: new SelectDynamicValueFunction(data =>
							getString(CUSTOMER_GROUP_STATUS_COLOR_CLASS, data.value)
						),
					},
					singleValueProps: {
						symbol: 'circle',
						className: new SelectDynamicValueFunction(data =>
							getString(CUSTOMER_GROUP_STATUS_COLOR_CLASS, data.value)
						),
					}
				}
			),
			new SimpleStaticSearchOptionObject(
				'name',
				this.tt('nameField', 'ItemPopup.MainTab')
			),
		];
		
		return this.renderLayout((
			<div id={this.getDomId()} className={`${this.getOption('domPrefix')}`}>
				{
					this.hasTranslation('page_short_description') && this.t('page_short_description') ?
						<div className="simple-page-description">
							<Label content={this.t('page_short_description')} supportHtml={true} />
						</div>
						: null
				}

				<SimpleStaticSearch
					styleName="compact"
					className="main-search rows-3"
					defaultCollapse={true}
					layout={SIMPLE_STATIC_SEARCH_LAYOUT.STACKED}
					buttonProps={{
						displayStyle: BUTTON_STYLE.DEFAULT
					}}
					options={filterOptions}
					value={mainListFilter}
					title={(<Label icon="search" content={this.t('Search', 'general')} />)}
					applied={!this.isMainListFilterEmpty()}
					enableToolbar={true}
					enableResetButton={false}
					onChange={this.filterMainList}
					onRemove={this.removeMainListFilter}
					onToggle={visible => this.setState({limitToAvailableSpace: visible})}
					ref={node => { this.mainListFilterRef = node; }}
				/>

				<DataTable
					id="main-page-table"
					className="standard sticky-last-column"
					limitToAvailableSpace={limitToAvailableSpace && !isMobileBreakpoint}
					highlightOnHover={true}
					columns={[
						{
							name: 'status',
							label: this.tt('statusField', 'ItemPopup.MainTab'),
							dataType: DATA_TABLE_CELL_TYPE.ANY,
							dataTypeOptions: new DataTableCellAnyTypeOptionsDataObject({
								content: row => (
									<Label
										element="span"
										elementProps={{className: `tag ${CUSTOMER_GROUP_STATUS_BKG_COLOR_CLASS[row.status]}`}}
										content={!!row.status ? this.tt(row.status, 'ItemPopup.MainTab.status') : '—'}
									/>
								)
							}),
							width: 1,
						},
						{
							name: 'name',
							label: this.tt('nameField', 'ItemPopup.MainTab')
						},
						{
							name: 'projectName',
							label: this.tt('projectField', 'ItemPopup.MainTab')
						},
						{
							name: 'customerCount',
							label: this.t('customerCountColumn'),
							dataType: DATA_TABLE_CELL_TYPE.NUMBER,
							dataTypeOptions: new DataTableCellNumberTypeOptionsDataObject({
								
							}),
							defaultValue: '—',
							emptyAsDefault: true,
						},
						
						{
							dataType: DATA_TABLE_CELL_TYPE.ANY,
							dataTypeOptions: new DataTableCellAnyTypeOptionsDataObject({
								standardWrapper: false,
								content: this.renderActions,
							}),
							stopPropagation: true,
							width: 1,
						}
					]}
					onRowClick={data => this.redirectToItem(data.id)}
					data={mainList}
					paginationType={PAGINATION_TYPE.STATIC}
					onSortByColumn={this.sortMainList}
					onPaginationClick={this.loadMainListPage}
					{...mainListPagination}
					{...mainListSort}
				/>
			</div>
		), undefined, undefined, {
			app: appConfig,
			mainSidebarShrank,
			toggleMainSidebarSizeAction,
		});
	}
}

export * from "./config";
export default connect(mapStateToProps, getPageActions({...actions, ...popupActions}))(CustomerGroupsPage);